// Row-major 3x3 matrices, flat and immutable.

import type { Vector3 } from './vector3.ts'

// f64: narrowing to fp32 belongs at the tensor boundary, not inside a math primitive.
export type Matrix3 = Readonly<Float64Array>

export const IDENTITY: Matrix3 = Float64Array.from([1, 0, 0, 0, 1, 0, 0, 0, 1])

// Rows as they appear in JSON: [[r00,r01,r02],[r10,...],[r20,...]].
export function fromRows(rows: unknown, what: string): Matrix3 {
  if (!Array.isArray(rows) || rows.length !== 3) {
    throw new Error(`matrix3.fromRows: ${what} is not 3 rows`)
  }
  const flat = new Float64Array(9)
  for (let row = 0; row < 3; row++) {
    const values = rows[row]
    if (!Array.isArray(values) || values.length !== 3) {
      throw new Error(`matrix3.fromRows: ${what} row ${row} is not 3 values`)
    }
    for (let column = 0; column < 3; column++) {
      const value = Number(values[column])
      if (!Number.isFinite(value)) throw new Error(`matrix3.fromRows: ${what}[${row}][${column}] is not finite`)
      flat[row * 3 + column] = value
    }
  }
  return flat
}

export function transform(matrix: Matrix3, vector: Vector3): Vector3 {
  return [
    matrix[0] * vector[0] + matrix[1] * vector[1] + matrix[2] * vector[2],
    matrix[3] * vector[0] + matrix[4] * vector[1] + matrix[5] * vector[2],
    matrix[6] * vector[0] + matrix[7] * vector[1] + matrix[8] * vector[2],
  ]
}

// Transpose-multiply, i.e. the inverse for a rotation — world to the matrix's frame.
export function transformTransposed(matrix: Matrix3, vector: Vector3): Vector3 {
  return [
    matrix[0] * vector[0] + matrix[3] * vector[1] + matrix[6] * vector[2],
    matrix[1] * vector[0] + matrix[4] * vector[1] + matrix[7] * vector[2],
    matrix[2] * vector[0] + matrix[5] * vector[1] + matrix[8] * vector[2],
  ]
}

// The first two COLUMNS, row by row — the 6D rotation encoding the eye decoders take.
export function firstTwoColumns(matrix: Matrix3): number[] {
  return [matrix[0], matrix[1], matrix[3], matrix[4], matrix[6], matrix[7]]
}
